import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBikes, adminDeleteBike } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Star } from "@phosphor-icons/react";

const COLLECTION_TABS = [
  { key: "all", label: "Όλες" },
  { key: "Thrust", label: "Thrust" },
  { key: "F.B. Mondial", label: "F.B. Mondial" },
  { key: "Asus", label: "Asus" },
  { key: "RDB Electric", label: "RDB Electric" },
  { key: "_none", label: "Χωρίς σειρά" },
];

export default function BikesManager() {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await fetchBikes();
      setBikes(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    if (tab === "all") return bikes;
    if (tab === "_none") return bikes.filter((b) => !b.collection);
    return bikes.filter((b) => b.collection === tab);
  }, [bikes, tab]);

  const onDelete = async (id) => {
    if (!window.confirm("Οριστική διαγραφή;")) return;
    try {
      await adminDeleteBike(id);
      toast.success("Διαγράφηκε");
      refresh();
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  return (
    <div data-testid="bikes-manager" className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-overline mb-3">Κατάλογος</p>
          <h1 className="font-display font-black uppercase text-4xl tracking-tight">Διαχείριση μηχανών</h1>
          <p className="text-white/55 text-sm mt-2">{bikes.length} μηχανές δημοσιευμένες</p>
        </div>
        <Link to="/dashboard/bikes/new" data-testid="new-bike-btn" className="btn-primary text-xs">
          <Plus size={14} weight="bold" className="mr-2" /> Νέα μηχανή
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10">
        {COLLECTION_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`bikes-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === t.key ? "text-[#E50000] border-b-2 border-[#E50000]" : "text-white/60 hover:text-white"
            }`}
          >
            {t.label} {t.key !== "all" ? `(${bikes.filter((b) => t.key === "_none" ? !b.collection : b.collection === t.key).length})` : `(${bikes.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="border border-white/10 p-10 text-white/50">Φόρτωση...</div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 p-10 text-white/50">Καμία μηχανή σε αυτή τη σειρά.</div>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#141414]">
              <tr className="text-left text-overline">
                <th className="p-4">Εικόνα</th>
                <th className="p-4">Μάρκα / Μοντέλο</th>
                <th className="p-4">Σειρά</th>
                <th className="p-4">Έτος</th>
                <th className="p-4">Κατηγορία</th>
                <th className="p-4">Τιμή</th>
                <th className="p-4">Κατάσταση</th>
                <th className="p-4">Featured</th>
                <th className="p-4">Coming Soon</th>
                <th className="p-4 text-right">Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} data-testid={`bike-row-${b.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4">
                    <img src={b.image_url} alt={b.model} className="w-16 h-12 object-cover" />
                  </td>
                  <td className="p-4">
                    <p className="font-display font-bold uppercase text-white">{b.brand}</p>
                    <p className="text-white/60 text-xs">{b.model}</p>
                  </td>
                  <td className="p-4">
                    {b.collection ? (
                      <span className="text-[10px] uppercase tracking-wider bg-[#E50000] text-white px-2 py-1">{b.collection}</span>
                    ) : (
                      <span className="text-white/30 text-xs">—</span>
                    )}
                  </td>
                  <td className="p-4 font-mono text-white/70">{b.year}</td>
                  <td className="p-4 text-white/70">{b.category}</td>
                  <td className="p-4 font-mono text-white">€{b.price.toLocaleString()}</td>
                  <td className="p-4 text-white/70">{b.condition === "New" ? "Καινούργιο" : "Μεταχ."}</td>
                  <td className="p-4">{b.featured && <Star size={16} weight="fill" className="text-[#E50000]" />}</td>
                  <td className="p-4">{b.coming_soon && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#E50000] text-white px-2 py-1">Coming Soon</span>}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      <Link
                        to={`/dashboard/bikes/${b.id}/edit`}
                        data-testid={`edit-bike-${b.id}`}
                        className="w-8 h-8 border border-white/15 flex items-center justify-center hover:border-[#E50000] hover:text-[#E50000]"
                        title="Επεξεργασία"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </Link>
                      <button
                        onClick={() => onDelete(b.id)}
                        data-testid={`delete-bike-${b.id}`}
                        className="w-8 h-8 border border-white/15 flex items-center justify-center hover:border-[#E50000] hover:text-[#E50000]"
                        title="Διαγραφή"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
